/**
 * BaseCanvas - Core canvas functionality for both Play and Concept editors
 * Extracted from Canvas.tsx following DRY principles
 *
 * Includes:
 * - FootballField rendering
 * - SVGCanvas with EditablePlayRenderer
 * - Player layer with drag/click/hover
 * - Zoom/pan controls
 * - Custom cursors
 * - Pre-snap motion system
 * - All drawing tools
 *
 * Does NOT include:
 * - History/undo (PlayCanvas adds this)
 * - Dialog components themselves (via render props)
 * - Context connections (via callbacks)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDialogAutoClose, useFieldCoordinates } from '@/hooks'
import { PLAYER_RADIUS_FEET, TOOL_ADD_PLAYER, TOOL_DRAW, TOOL_ERASE, TOOL_FILL } from '@/constants'
import { DEFAULT_PLAYER_COLOR } from '@/constants/Formation.constants'
import { snapToGrid } from '@/utils/GridSnap.utils'
import { distance } from '@/utils'
import { SVGCanvas } from './SVGCanvas'
import { FootballField } from '../field/FootballField'
// Note: Player.tsx HTML component is deprecated. Players now render via SVG PlayersOverlay.
import { CanvasCursors } from './CanvasCursors'
import {
	useCanvasDimensions,
	useCanvasMouseHandlers,
	useCanvasZoomPan,
	useDrawingOperations,
	usePlayerOperations,
	usePreSnapMotion,
} from './hooks'
import { useCanvasViewport } from '@/contexts/CanvasViewportContext'
import { type AlignmentSegment, createLOSAlignmentSegment, findAlignmentSegments } from '@/utils/Alignment.utils'
import type { BaseCanvasActions, BaseCanvasProps, BaseCanvasState } from './BaseCanvas.types'
import type { PathStyle } from '@/types'
import './canvas.css'

// Layout constants
const HEADER_TOOLBAR_HEIGHT = 88

/**
 * BaseCanvas Component
 * Shared canvas functionality for both play and concept editors
 */
export function BaseCanvas({
							   drawingState,
							   drawings,
							   players,
							   callbacks,
							   selectedDrawingIds = [],
							   containerMode = 'viewport',
							   readonly: _readonly = false,
							   renderDialogs,
							   onPlayerMoveIntercept,
							   onBeforeStateChange,
							   onSelectionChange,
							   animationOverlay,
						   }: BaseCanvasProps) {
	const whiteboardRef = useRef<HTMLDivElement>(null)
	const fieldViewportRef = useRef<HTMLDivElement>(null)

	// Local drawings state for real-time drag updates.
	// Syncs from props but also updates during drag via onChange.
	const [localDrawings, setLocalDrawings] = useState(drawings)
	useEffect(() => {
		setLocalDrawings(drawings)
	}, [drawings])

	// Local players state for real-time drag updates (same pattern as drawings).
	// Syncs from props; handleMovePlayerOnly updates it immediately during drag.
	const [localPlayers, setLocalPlayers] = useState(players)
	const localPlayersRef = useRef(players)
	useEffect(() => {
		setLocalPlayers(players)
		localPlayersRef.current = players
	}, [players])

	// Internal state
	const [isHoveringDeletable, setIsHoveringDeletable] = useState(false)
	const [showLabelDialog, setShowLabelDialog] = useState(false)
	const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null)
	const [labelDialogPosition, setLabelDialogPosition] = useState({ x: 0, y: 0 })
	const [hoveredPlayerId, setHoveredPlayerId] = useState<string | null>(null)
	const [alignmentSegments, setAlignmentSegments] = useState<AlignmentSegment[]>([])

	// Canvas infrastructure hooks
	const canvasDimensions = useCanvasDimensions({ whiteboardRef: fieldViewportRef, showPlayBar: false })
	const coordSystem = useFieldCoordinates({
		containerWidth: canvasDimensions.width,
		containerHeight: canvasDimensions.height,
	})
	const { zoom, panX, panY, isPanning, panMode } = useCanvasViewport()

	// When a custom cursor tool is active (and not panning), add a CSS class
	// that forces cursor:none on all descendants, preventing child inline styles
	// from overriding the hidden cursor while the custom cursor overlay renders.
	const isCustomCursorActive = !isPanning && panMode !== 'spacebar' && [
		TOOL_DRAW, TOOL_ERASE, TOOL_FILL, TOOL_ADD_PLAYER,
	].includes(drawingState.tool)

	const zoomPanHandlers = useCanvasZoomPan({ whiteboardRef, canvasDimensions })

	// Mouse handling
	const {
		cursorPosition,
		cursorFeetPosition,
		isOverCanvas,
		handleMouseMove: baseHandleMouseMove,
		handleMouseEnter,
		handleMouseLeave,
		getCursorStyle,
		shouldShowCustomCursor,
	} = useCanvasMouseHandlers({
		drawingState,
		coordSystem,
		zoom,
		panX,
		panY,
		isPanning,
		panMode,
		zoomPanHandlers,
	})

	// Drawing operations
	const {
		editingDrawing,
		setEditingDrawing,
		handleDrawingSelectWithDialog,
		handleDeleteDrawing,
	} = useDrawingOperations({
		drawings,
		players,
		callbacks,
		onBeforeStateChange,
	})

	// Link drawing to player: updates local state immediately + persists to server.
	// Defined here (not in useDrawingOperations) because we need setLocalDrawings
	// for immediate visual feedback — callbacks.onDrawingsChange is a no-op in
	// BlockAwarePlayCanvas.
	const handleLinkDrawingToPlayer = useCallback(
		(drawingId: string, pointId: string, playerId: string, newPlayerOverride?: { x: number; y: number }) => {
			const player = newPlayerOverride || localPlayersRef.current.find(p => p.id === playerId)
			if (!player) return

			onBeforeStateChange?.()

			// Update local state immediately for visual feedback
			setLocalDrawings(prev => {
				// Don't link if player already linked to another drawing
				if (prev.some(d => d.playerId === playerId)) return prev

				return prev.map(d => {
					if (d.id !== drawingId || d.playerId) return d
					const linkedPoint = d.points[pointId]
					if (!linkedPoint) return d
					return {
						...d,
						playerId,
						linkedPointId: pointId,
						points: {
							...d.points,
							[pointId]: { ...linkedPoint, x: player.x, y: player.y },
						},
					}
				})
			})

			// Persist to server
			callbacks.onLinkDrawingToPlayer(drawingId, pointId, playerId)
		},
		[callbacks, onBeforeStateChange],
	)

	// Unlink a player from its drawing: nudge the control point away from the
	// player in the direction the drawing extends, then persist both the position
	// change and the unlink to the server.
	const UNLINK_NUDGE_FEET = 3
	const handleUnlinkDrawing = useCallback(
		(playerId: string) => {
			const linkedDrawing = localDrawings.find(d => d.playerId === playerId)
			if (!linkedDrawing || !linkedDrawing.linkedPointId) return

			const player = localPlayersRef.current.find(p => p.id === playerId)
			if (!player) return

			onBeforeStateChange?.()

			const pointId = linkedDrawing.linkedPointId

			// Find neighbor point to determine nudge direction
			let neighborPoint: { x: number; y: number } | null = null
			for (const segment of linkedDrawing.segments) {
				const idx = segment.pointIds.indexOf(pointId)
				if (idx === -1) continue
				const adjacentId = idx > 0 ? segment.pointIds[idx - 1] : segment.pointIds[idx + 1]
				if (adjacentId) neighborPoint = linkedDrawing.points[adjacentId] ?? null
				break
			}

			// Compute nudge position: move along the drawing direction (toward neighbor)
			let newX: number, newY: number
			if (neighborPoint) {
				const dx = neighborPoint.x - player.x
				const dy = neighborPoint.y - player.y
				const dist = distance(player, neighborPoint)
				if (dist > 0) {
					newX = player.x + (dx / dist) * UNLINK_NUDGE_FEET
					newY = player.y + (dy / dist) * UNLINK_NUDGE_FEET
				} else {
					newX = player.x
					newY = player.y - UNLINK_NUDGE_FEET
				}
			} else {
				newX = player.x
				newY = player.y - UNLINK_NUDGE_FEET
			}

			// Update local state: clear link and move control point
			setLocalDrawings(prev => prev.map(d => {
				if (d.id !== linkedDrawing.id) return d
				const existingPoint = d.points[pointId]
				if (!existingPoint) return d
				return {
					...d,
					playerId: undefined,
					linkedPointId: undefined,
					points: {
						...d.points,
						[pointId]: { ...existingPoint, x: newX, y: newY },
					},
				}
			}))

			// Persist control point position, then unlink
			callbacks.onControlPointUpdate?.(linkedDrawing.id, pointId, newX, newY)
			callbacks.onUnlinkDrawingFromPlayer(linkedDrawing.id)
		},
		[localDrawings, callbacks, onBeforeStateChange],
	)

	// Pre-snap motion
	const {
		placementMode,
		setPlacementMode,
		handlePreSnapMovement: _handlePreSnapMovement,
		handlePreSnapPlacementClick,
	} = usePreSnapMotion({
		drawings,
		players,
		callbacks,
		editingDrawing,
		onBeforeStateChange,
	})

	// Player operations
	const {
		handleCanvasClickForPlayer,
		handlePlayerPositionChange,
		handlePlayerLabelClick,
		handlePlayerDelete,
		handleFillPlayer,
		isPlayerInteractable,
	} = usePlayerOperations({
		players,
		drawings,
		drawingState,
		coordSystem,
		callbacks,
		zoom,
		panX,
		panY,
		onBeforeStateChange,
		onPlayerMoveIntercept,
		onLinkDrawingToPlayer: handleLinkDrawingToPlayer,
	})

	// Calculate stroke width in feet
	const strokeFeet =
		coordSystem.scale > 0
			? drawingState.brushSize / coordSystem.scale
			: drawingState.brushSize

	const defaultPathStyle: PathStyle = {
		color: drawingState.color,
		strokeWidth: strokeFeet,
		brushSize: drawingState.brushSize,
		lineStyle: drawingState.lineStyle,
		lineEnd: drawingState.lineEnd,
		pathMode: drawingState.pathMode,
	}

	// === ALIGNMENT GUIDELINES ===
	useEffect(() => {
		// Case 1: Add player tool cursor - show alignment for where new player would be placed
		if (cursorFeetPosition && drawingState.tool === TOOL_ADD_PLAYER && isOverCanvas) {
			const virtualPlayer = { id: 'cursor', x: cursorFeetPosition.x, y: cursorFeetPosition.y }
			const otherPlayers = localPlayers.filter((p) => !p.isGhost)
			const segments = findAlignmentSegments(virtualPlayer, otherPlayers)

			const losSegment = createLOSAlignmentSegment(virtualPlayer, otherPlayers)
			if (losSegment) {
				segments.push(losSegment)
			}

			setAlignmentSegments(segments)
			return
		}

		// Case 2: Hovering over existing player
		if (hoveredPlayerId) {
			const hoveredPlayer = localPlayers.find((p) => p.id === hoveredPlayerId)
			if (!hoveredPlayer) {
				setAlignmentSegments([])
				return
			}

			const otherPlayers = localPlayers.filter(
				(p) => p.id !== hoveredPlayerId && !p.isGhost,
			)

			const segments = findAlignmentSegments(hoveredPlayer, otherPlayers)

			const losSegment = createLOSAlignmentSegment(hoveredPlayer, otherPlayers)
			if (losSegment) {
				segments.push(losSegment)
			}

			setAlignmentSegments(segments)
			return
		}

		setAlignmentSegments([])
	}, [hoveredPlayerId, localPlayers, cursorFeetPosition, drawingState.tool, isOverCanvas])

	// === EVENT HANDLERS ===

	const handleMouseMove = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			baseHandleMouseMove(e, whiteboardRef)
		},
		[baseHandleMouseMove],
	)

	const handleCanvasClick = useCallback(
		async (e: React.MouseEvent<HTMLDivElement>) => {
			if (drawingState.tool !== TOOL_ADD_PLAYER) return

			const newPlayer = await handleCanvasClickForPlayer(e, whiteboardRef)
			if (newPlayer) {
				setSelectedPlayerId(newPlayer.id)
				setLabelDialogPosition({ x: e.clientX, y: e.clientY })
				setShowLabelDialog(true)
			}
		},
		[drawingState.tool, handleCanvasClickForPlayer],
	)

	const handlePlayerLabelClickWithDialog = useCallback(
		(id: string, x: number, y: number) => {
			const result = handlePlayerLabelClick(id, x, y)
			if (result) {
				setSelectedPlayerId(result.id)
				setLabelDialogPosition({ x: result.x, y: result.y })
				setShowLabelDialog(true)
			}
		},
		[handlePlayerLabelClick],
	)

	const _handlePlayerLabelChange = useCallback(
		(label: string) => {
			if (selectedPlayerId) {
				callbacks.onPlayerLabelChange(selectedPlayerId, label)
			}
		},
		[selectedPlayerId, callbacks],
	)

	const _handlePlayerDeleteFromDialog = useCallback(() => {
		if (selectedPlayerId) {
			callbacks.onPlayerDelete(selectedPlayerId)
			setShowLabelDialog(false)
			setSelectedPlayerId(null)
		}
	}, [selectedPlayerId, callbacks])

	const handlePlayerDeleteById = useCallback(
		(id: string) => {
			handlePlayerDelete(id)
			setIsHoveringDeletable(false)
			if (selectedPlayerId === id) {
				setShowLabelDialog(false)
				setSelectedPlayerId(null)
			}
		},
		[handlePlayerDelete, selectedPlayerId],
	)

	const handleMovePlayerOnly = useCallback(
		(id: string, x: number, y: number) => {
			// Compute delta from current local position for linked drawing movement
			const current = localPlayersRef.current.find(p => p.id === id)
			const dx = current ? x - current.x : 0
			const dy = current ? y - current.y : 0

			// Update ref immediately for subsequent calls in same frame (group movement)
			localPlayersRef.current = localPlayersRef.current.map(p =>
				p.id === id ? { ...p, x, y } : p
			)

			// Update React state (functional form for batching multiple calls)
			setLocalPlayers(prev => prev.map(p =>
				p.id === id ? { ...p, x, y } : p
			))

			// Move linked drawings in local state for visual consistency
			if (dx !== 0 || dy !== 0) {
				setLocalDrawings(prev => prev.map(d => {
					if (d.playerId !== id) return d
					const newPoints: typeof d.points = {}
					for (const [pointId, point] of Object.entries(d.points)) {
						newPoints[pointId] = { ...point, x: point.x + dx, y: point.y + dy }
					}
					return { ...d, points: newPoints }
				}))
			}

			// Propagate to server persistence
			callbacks.onPlayerMove(id, x, y)
		},
		[callbacks],
	)

	const handleAddPlayerAtNode = useCallback(
		async (drawingId: string, pointId: string, x: number, y: number) => {
			const drawing = drawings.find((d) => d.id === drawingId)
			if (drawing?.playerId) return

			onBeforeStateChange?.()

			// Snap node position to valid grid position (enforces LOS exclusion zone)
			const snapped = snapToGrid(x, y)

			const newPlayer = {
				id: crypto.randomUUID(),
				x: snapped.x,
				y: snapped.y,
				label: '',
				color: DEFAULT_PLAYER_COLOR,
			}

			// Get server-assigned ID and use it for selectedPlayerId
			const serverId = await callbacks.onPlayerAdd(newPlayer)
			handleLinkDrawingToPlayer(drawingId, pointId, serverId, newPlayer)

			setSelectedPlayerId(serverId)
			const pixelPos = coordSystem.feetToPixels(x, y)
			const rect = whiteboardRef.current?.getBoundingClientRect()
			setLabelDialogPosition({
				x: pixelPos.x + (rect?.left || 0),
				y: pixelPos.y + (rect?.top || 0),
			})
			setShowLabelDialog(true)
		},
		[drawings, callbacks, coordSystem, handleLinkDrawingToPlayer, onBeforeStateChange],
	)

	const handlePlayerLinkToNode = useCallback(
		(playerId: string, drawingId: string, pointId: string) => {
			handleLinkDrawingToPlayer(drawingId, pointId, playerId)
		},
		[handleLinkDrawingToPlayer],
	)

	// Get all lineman IDs for group movement (when any lineman is dragged, all move together)
	const getLinemanGroup = useCallback(
		(blockId: string): string[] => {
			const player = localPlayers.find((p) => p.id === blockId)
			if (!player?.isLineman) return [blockId]
			// Return all lineman IDs for group movement
			return localPlayers.filter((p) => p.isLineman).map((p) => p.id)
		},
		[localPlayers],
	)

	// Handle player hover change for alignment guidelines
	const handlePlayerHoverChange = useCallback(
		(id: string, isHovered: boolean) => {
			if (drawingState.tool === TOOL_ERASE) {
				setIsHoveringDeletable(isHovered)
			}
			setHoveredPlayerId(isHovered ? id : null)
		},
		[drawingState.tool],
	)

	// === RENDERING ===

	const scale = coordSystem.scale
	const playerCursorDiameter = PLAYER_RADIUS_FEET * 2 * scale

	// Pre-compute ghost player display properties
	const playerDisplayProps = useMemo(() => {
		const propsMap = new Map<string, { label: string; color: string }>()

		// Defensive check: ensure players is an array
		if (!Array.isArray(players)) {
			return propsMap
		}

		players.forEach((player) => {
			if (player.isGhost && player.sourcePlayerId) {
				const sourcePlayer = players.find((p) => p.id === player.sourcePlayerId)
				if (sourcePlayer) {
					propsMap.set(player.id, {
						label: sourcePlayer.label,
						color: sourcePlayer.color,
					})
				} else {
					propsMap.set(player.id, {
						label: player.label,
						color: player.color,
					})
				}
			} else {
				propsMap.set(player.id, {
					label: player.label,
					color: player.color,
				})
			}
		})

		return propsMap
	}, [players])

	const containerClasses = `canvas-container ${
		containerMode === 'fill' ? 'canvas-container-fill' : 'canvas-container-viewport'
	}`

	// Stable callback for closing the label dialog
	const closeLabelDialog = useCallback(() => setShowLabelDialog(false), [])

	// Auto-close PlayerLabelDialog when cursor moves away
	useDialogAutoClose({
		isOpen: showLabelDialog,
		onClose: closeLabelDialog,
		dataAttribute: 'data-player-label-dialog',
		bufferSize: 50,
	})

	// Build state and actions for render props
	const baseCanvasState: BaseCanvasState = {
		selectedPlayerId,
		showLabelDialog,
		labelDialogPosition,
		editingDrawing,
		placementMode,
		coordSystem,
		canvasDimensions,
		cursorPosition,
		isOverCanvas,
		isHoveringDeletable,
	}

	const baseCanvasActions: BaseCanvasActions = {
		setSelectedPlayerId,
		setShowLabelDialog,
		setEditingDrawing,
		setPlacementMode,
		onUnlinkDrawing: handleUnlinkDrawing,
	}

	return (
		<div className={containerClasses}>
			{/* Whiteboard frame with field background */}
			<div
				ref={whiteboardRef}
				className={`canvas-whiteboard${isCustomCursorActive ? ' custom-cursor-active' : ''}`}
				style={{
					cursor: getCursorStyle(),
					height:
						containerMode === 'fill'
							? '100%'
							: `calc(100vh - ${HEADER_TOOLBAR_HEIGHT}px)`,
					overflow: 'hidden',
				}}
				onMouseMove={handleMouseMove}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onMouseDown={zoomPanHandlers.handleMouseDown}
				onMouseUp={zoomPanHandlers.handleMouseUp}
				onClick={handleCanvasClick}
			>
				{/* Field viewport - single source of truth for dimensions */}
				<div ref={fieldViewportRef} className="field-viewport">
					{/* Transform container for zoom/pan */}
					<div
						className={`canvas-transform-container${animationOverlay ? ' canvas-transform-hidden' : ''}`}
						style={{
							transform: `scale(${zoom}) translate(${panX / zoom}px, ${panY / zoom}px)`,
						}}
					>
						{/* Field background */}
						<FootballField
							width={canvasDimensions.width}
							height={canvasDimensions.height}
						/>

						{/* SVG layer for structured drawings */}
						<div className="canvas-svg-layer">
							<SVGCanvas
								width={canvasDimensions.width}
								height={canvasDimensions.height}
								coordSystem={coordSystem}
								drawings={localDrawings}
								players={localPlayers}
								selectedDrawingIds={selectedDrawingIds}
								onChange={setLocalDrawings}
								activeTool={
									drawingState.tool === TOOL_DRAW
										? 'draw'
										: drawingState.tool === TOOL_ERASE
											? 'erase'
											: drawingState.tool === TOOL_ADD_PLAYER
												? 'addPlayer'
												: drawingState.tool === TOOL_FILL
													? 'fill'
													: 'select'
								}
								onDeleteDrawing={handleDeleteDrawing}
								eraseSize={drawingState.eraseSize}
								autoCorrect={true}
								defaultStyle={defaultPathStyle}
								snapThreshold={drawingState.snapThreshold}
								zoom={zoom}
								panX={panX}
								panY={panY}
								onLinkDrawingToPlayer={handleLinkDrawingToPlayer}
								onAddPlayerAtNode={handleAddPlayerAtNode}
								placementMode={placementMode}
								onPreSnapPlacementClick={handlePreSnapPlacementClick}
								onMovePlayer={handleMovePlayerOnly}
								alignmentSegments={alignmentSegments}
								isOverCanvas={isOverCanvas}
								cursorPosition={cursorPosition}
								onSelectWithPosition={handleDrawingSelectWithDialog}
								onBeforeStateChange={onBeforeStateChange}
								onSelectionChange={onSelectionChange}
								// SVG-based player rendering (unified block architecture)
								renderPlayersInSvg={true}
								onPlayerDelete={handlePlayerDeleteById}
								onPlayerFill={handleFillPlayer}
								onPlayerLabelClick={handlePlayerLabelClickWithDialog}
								onPlayerLinkToNode={handlePlayerLinkToNode}
								onPlayerHoverChange={handlePlayerHoverChange}
								getLinemanGroup={getLinemanGroup}
							onDrawingAdd={callbacks.onDrawingAdd}
							onDrawingUpdate={callbacks.onDrawingUpdate}
							onControlPointUpdate={callbacks.onControlPointUpdate}
							/>
						</div>
						{/* Players now render inside SVGCanvas via PlayersOverlay */}
					</div>
					{/* End of canvas-transform-container */}
					{animationOverlay}
				</div>
				{/* End of field-viewport */}

				{/* Cursor overlay - OUTSIDE transform so cursors stay at mouse position */}
				<div className="canvas-cursor-overlay">
					<CanvasCursors
						drawingState={drawingState}
						cursorPosition={cursorPosition}
						isOverCanvas={isOverCanvas}
						isHoveringDeletable={isHoveringDeletable}
						shouldShowCustomCursor={shouldShowCustomCursor()}
						playerCursorDiameter={playerCursorDiameter}
					/>
				</div>
			</div>
			{/* End of canvas-whiteboard */}

			{/* Dialogs via render props */}
			{renderDialogs?.(baseCanvasState, baseCanvasActions)}
		</div>
	)
}
